import { useEffect, useRef, useState } from 'react';

/**
 * Rich Text.dc.html: the collection description. Viewing: the HTML in the
 * `.rt` styles (15px, line-height 1.5, #3f3f44), click to edit; empty and
 * editable shows a "+ Add a description" affordance. Editing: a bordered box
 * with a small toolbar (B, I, H, •, 1., ↗) over a contenteditable; Esc or
 * Cmd/Ctrl+Enter or blur saves; an emptied editor calls onCancel.
 *
 * Formatting uses document.execCommand, as the prototype does. It is
 * deprecated but universally implemented, and the alternative (a selection
 * model) buys nothing here: the server sanitizes to the same six tags.
 */
const TOOLS: ReadonlyArray<{ cmd: string; label: string; title: string; italic?: boolean }> = [
  { cmd: 'bold', label: 'B', title: 'Bold' },
  { cmd: 'italic', label: 'I', title: 'Italic', italic: true },
  { cmd: 'h2', label: 'H', title: 'Heading' },
  { cmd: 'insertUnorderedList', label: '•', title: 'Bullet list' },
  { cmd: 'insertOrderedList', label: '1.', title: 'Numbered list' },
  { cmd: 'link', label: '↗', title: 'Link' },
];

export function RichText({
  html,
  editable,
  startEditing,
  placeholder = 'Add a description',
  onChange,
  onCancel,
}: {
  html: string;
  editable: boolean;
  startEditing?: boolean;
  placeholder?: string;
  onChange?: (html: string) => void;
  onCancel?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [fmt, setFmt] = useState<Record<string, boolean>>({});
  const ref = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const start = () => {
    if (!editable) return;
    setEditing(true);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (el) {
        el.innerHTML = html || '';
        el.focus();
      }
    });
  };
  useEffect(() => {
    if (startEditing && !editing) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startEditing]);

  const save = () => {
    const el = ref.current;
    let next = el ? el.innerHTML : '';
    if (!el || !el.textContent?.trim()) next = '';
    setEditing(false);
    if (!next) {
      onCancel?.();
      if (html) onChange?.('');
      return;
    }
    if (next !== (html || '')) onChange?.(next);
    else onCancel?.();
  };

  const refreshFmt = () => {
    setFmt({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      h2: (document.queryCommandValue('formatBlock') || '').toLowerCase() === 'h2',
    });
  };

  const exec = (cmd: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (cmd === 'link') {
      const url = window.prompt('Link URL', 'https://');
      ref.current?.focus();
      if (!url) return;
      document.execCommand('createLink', false, url);
    } else if (cmd === 'h2') {
      const isH = document.queryCommandValue('formatBlock').toLowerCase() === 'h2';
      document.execCommand('formatBlock', false, isH ? 'p' : 'h2');
    } else {
      document.execCommand(cmd, false);
    }
    refreshFmt();
  };

  if (editing) {
    return (
      <div ref={boxRef} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', border: '1px solid #d4d4d2', borderRadius: 8, background: '#fff', margin: '0 -8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 6, borderBottom: '1px solid #ebebea' }}>
          {TOOLS.map((t) => (
            <button
              key={t.cmd}
              type="button"
              title={t.title}
              className="tr-hover-track-ink"
              onMouseDown={exec(t.cmd)}
              style={{
                display: 'grid',
                placeItems: 'center',
                minWidth: 28,
                height: 28,
                padding: '0 6px',
                border: 0,
                borderRadius: 5,
                background: fmt[t.cmd] ? '#0f0f10' : 'transparent',
                color: fmt[t.cmd] ? '#fff' : '#3f3f44',
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                fontStyle: t.italic ? 'italic' : 'normal',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ padding: '10px 14px', fontSize: 15, lineHeight: 1.5, color: '#3f3f44', minHeight: 64 }}>
          <div
            ref={ref}
            className="rt"
            contentEditable
            suppressContentEditableWarning
            data-placeholder={placeholder}
            style={{ minHeight: 72, outline: 'none' }}
            onKeyDown={(e) => {
              if (e.key === 'Escape' || (e.key === 'Enter' && (e.metaKey || e.ctrlKey))) {
                e.preventDefault();
                save();
              }
            }}
            onBlur={(e) => {
              if (e.relatedTarget && boxRef.current?.contains(e.relatedTarget as Node)) return;
              save();
            }}
            onKeyUp={refreshFmt}
            onMouseUp={refreshFmt}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    );
  }

  if (!html && editable) {
    return (
      <div
        title="Click to add"
        className="tr-hover-soft-muted"
        onClick={(e) => {
          e.stopPropagation();
          start();
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 -8px', padding: '4px 7px', borderRadius: 6, fontSize: 15, color: '#8a8a8f', cursor: 'text', transition: 'background .12s' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M12 5v14M5 12h14" />
        </svg>
        {placeholder}
      </div>
    );
  }
  if (!html) return null;
  return (
    <div
      title={editable ? 'Click to edit' : undefined}
      className={editable ? 'rt tr-hover-soft' : 'rt'}
      onClick={(e) => {
        e.stopPropagation();
        start();
      }}
      style={{ margin: '0 -8px', padding: '6px 7px', borderRadius: 6, fontSize: 15, lineHeight: 1.5, color: '#3f3f44', cursor: editable ? 'text' : 'default', transition: 'background .12s' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Plain text of an HTML description, for cards and search. */
export function plainText(html: string | null | undefined): string {
  if (!html) return '';
  // DOMParser, not a scratch element's innerHTML: an inert document loads no
  // images and runs no handlers, so a stored `<img onerror>` stays text.
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}
